import { useEffect, useState } from "react";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { api } from "../lib/api";
import { useResource, titleCase } from "../lib/hooks";
import { EntityForm, Field } from "../components/EntityForm";
import { JOB_TITLES } from "../lib/formOptions";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ProviderOrgChart } from "../components/ProviderOrgChart";
import type { Company, Contact } from "../lib/types";

const EDITABLE: Array<{ key: keyof Company & string; label: string; half?: boolean }> = [
    { key: "primary_contact", label: "Primary contact", half: true },
    { key: "phone", label: "Phone", half: true },
    { key: "email", label: "Email", half: true },
    { key: "billing_email", label: "Billing email", half: true },
    { key: "website", label: "Website", half: true },
    { key: "address", label: "Address", half: true },
    { key: "suburb", label: "Suburb", half: true },
    { key: "postcode", label: "Postcode", half: true },
    { key: "notes", label: "Notes" },
];

type Dialog =
    | { kind: "createContact" }
    | { kind: "editContact"; contact: Contact }
    | { kind: "deleteContact"; contact: Contact }
    | null;

/**
 * The partner's own organisation record — the same company and contact rows
 * the CRM's Companies and Contacts pages show, maintained by the people the
 * details actually belong to. Commercial fields (type, NDIS registration,
 * ABN) are read-only facts here: the API's partner column allowlist refuses
 * them no matter what the browser sends.
 */
export const PartnerOrganisation = () => {
    const companies = useResource<Company[]>("/companies");
    const contacts = useResource<Contact[]>("/contacts");
    const company = companies.data?.[0];

    const [draft, setDraft] = useState<Record<string, string>>({});
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [dialog, setDialog] = useState<Dialog>(null);

    useEffect(() => {
        if (!company) return;
        const next: Record<string, string> = {};
        for (const field of EDITABLE) next[field.key] = (company[field.key] as string | null) ?? "";
        setDraft(next);
    }, [company]);

    const save = async () => {
        if (!company) return;
        setBusy(true);
        setMessage(null);
        try {
            const payload: Record<string, string | null> = {};
            for (const field of EDITABLE) payload[field.key] = draft[field.key]?.trim() || null;
            await api.put(`/companies/${company.id}`, payload);
            setMessage("Saved — this is what TrustCare's CRM now shows for your organisation.");
            companies.reload();
        } catch (err) {
            setMessage(err instanceof Error ? err.message : "Could not save");
        } finally {
            setBusy(false);
        }
    };

    const contactFields: Field[] = [
        { name: "first_name", label: "First name", kind: "text", required: true, half: true },
        { name: "last_name", label: "Last name", kind: "text", required: true, half: true },
        { name: "job_title", label: "Job title", kind: "text", half: true, suggestions: JOB_TITLES },
        { name: "phone", label: "Phone", kind: "tel", half: true },
        { name: "email", label: "Email", kind: "email" },
    ];

    if (companies.error) return <div className="error-banner">{companies.error}</div>;
    if (!company) return <p style={{ color: "var(--muted)" }}>Loading…</p>;

    return (
        <div style={{ display: "grid", gap: "1rem", maxWidth: 780 }}>
            {dialog?.kind === "createContact" && (
                <EntityForm title="Add contact" path="/contacts" fields={contactFields}
                    subtitle="People at your organisation TrustCare may need to reach."
                    initial={{ company_id: company.id }}
                    onClose={() => setDialog(null)} onSaved={() => { setDialog(null); contacts.reload(); }} />
            )}
            {dialog?.kind === "editContact" && (
                <EntityForm title="Edit contact" path="/contacts"
                    fields={contactFields} initial={dialog.contact}
                    onClose={() => setDialog(null)} onSaved={() => { setDialog(null); contacts.reload(); }} />
            )}
            {dialog?.kind === "deleteContact" && (
                <ConfirmDialog
                    title={`Remove ${dialog.contact.first_name} ${dialog.contact.last_name}?`}
                    tone="danger"
                    message="The contact is removed from your organisation's record."
                    confirmLabel="Remove contact"
                    request={{ method: "delete", path: `/contacts/${dialog.contact.id}` }}
                    onClose={() => setDialog(null)} onDone={() => { setDialog(null); contacts.reload(); }} />
            )}

            <div className="card">
                <div className="card-head"><h2>{company.name}</h2></div>
                <div className="card-body">
                    <dl className="kv">
                        <dt>Type</dt><dd>{titleCase(company.kind)}</dd>
                        <dt>NDIS registered</dt><dd>{company.ndis_registered ? "Yes" : "No"}</dd>
                        <dt>ABN</dt><dd>{company.abn ?? "—"}</dd>
                    </dl>
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.6rem" }}>
                        Organisation name, type, registration and ABN are maintained by TrustCare —
                        contact us if any of these need correcting.
                    </p>
                </div>
            </div>

            <div className="card">
                <div className="card-head"><h2>Contact details</h2></div>
                <div className="card-body">
                    <div className="form-grid">
                        {EDITABLE.map((field) => (
                            <div key={field.key} className={`field${field.half ? " half" : ""}`}>
                                <label htmlFor={`org-${field.key}`}>{field.label}</label>
                                {field.key === "notes" ? (
                                    <textarea id={`org-${field.key}`} rows={3}
                                        value={draft[field.key] ?? ""}
                                        onChange={(e) => setDraft((d) => ({ ...d, [field.key]: e.target.value }))} />
                                ) : (
                                    <input id={`org-${field.key}`}
                                        value={draft[field.key] ?? ""}
                                        onChange={(e) => setDraft((d) => ({ ...d, [field.key]: e.target.value }))} />
                                )}
                            </div>
                        ))}
                    </div>
                    <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", marginTop: "0.8rem" }}>
                        <button className="btn" onClick={() => void save()} disabled={busy}>
                            {busy ? "Saving…" : "Save organisation details"}
                        </button>
                        {message && <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{message}</span>}
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-head">
                    <h2>People at your organisation</h2>
                    <button className="btn small" onClick={() => setDialog({ kind: "createContact" })}>+ Add contact</button>
                </div>
                <div className="card-body flush"><div className="table-wrap">
                    <table>
                        <thead><tr><th>Name</th><th>Job title</th><th>Email</th><th>Phone</th>
                            <th style={{ textAlign: "right" }}>Actions</th></tr></thead>
                        <tbody>
                            {(contacts.data ?? []).map((contact) => (
                                <tr key={contact.id}>
                                    <td>
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontWeight: 600 }}>
                                            <InitialsAvatar name={`${contact.first_name} ${contact.last_name}`} size={26} />
                                            {contact.first_name} {contact.last_name}
                                        </div>
                                    </td>
                                    <td>{contact.job_title ?? "—"}</td>
                                    <td>{contact.email ?? "—"}</td>
                                    <td>{contact.phone ?? "—"}</td>
                                    <td><div className="row-actions">
                                        <button className="icon-btn" onClick={() => setDialog({ kind: "editContact", contact })}>Edit</button>
                                        <button className="icon-btn danger" onClick={() => setDialog({ kind: "deleteContact", contact })}>Remove</button>
                                    </div></td>
                                </tr>
                            ))}
                            {(contacts.data ?? []).length === 0 && (
                                <tr><td colSpan={5} className="empty">No contacts recorded yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div></div>
            </div>

            <div className="card">
                <div className="card-head"><h2>Your org chart</h2></div>
                <div className="card-body">
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.6rem" }}>
                        Built from the people above — the job title is the position on the card, and
                        each person's "reports to" sets the structure. It updates as you register
                        or change your people.
                    </p>
                    <ProviderOrgChart companyId={company.id} />
                </div>
            </div>
        </div>
    );
};
